// services/authAPI.js - Enhanced API service for authentication
import axios from 'axios'

// Create axios instance with default config
const api = axios.create({
  // Use relative URL for development (works with Vite proxy)
  // Use environment variable for production
  baseURL: import.meta.env.VITE_API_URL + "/api", 
  timeout: 30000, // Increased timeout for 2FA and social login
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token and device info
api.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const token = localStorage.getItem('access_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // Add device fingerprint for security
    const deviceId = localStorage.getItem('device_id') || generateDeviceId()
    config.headers['X-Device-ID'] = deviceId
    config.headers['X-Client-Version'] = import.meta.env.VITE_APP_VERSION || '1.0.0'
    config.headers['X-Platform'] = 'web'

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor to handle errors and token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    // Handle 401 Unauthorized
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      // Try to refresh token
      const refreshToken = localStorage.getItem('refresh_token')
      if (refreshToken) {
        try {
          const response = await api.post('/v1/auth/token/refresh', {
            refresh_token: refreshToken
          })

          const { access_token, refresh_token: newRefreshToken } = response.data
          localStorage.setItem('access_token', access_token)

          if (newRefreshToken) {
            localStorage.setItem('refresh_token', newRefreshToken)
          }

          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${access_token}`
          return api(originalRequest)

        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError)
          // Clear all session data and redirect to login
          authUtils.clearAuth()

          if (window.location.pathname !== '/auth/login') {
            localStorage.setItem('intended_route', window.location.pathname)
            window.location.href = '/auth/login'
          }
        }
      } else {
        // No refresh token, clear all session data and redirect to login
        authUtils.clearAuth()

        if (window.location.pathname !== '/auth/login') {
          localStorage.setItem('intended_route', window.location.pathname)
          window.location.href = '/auth/login'
        }
      }
    }

    // Handle rate limiting
    if (error.response?.status === 429) {
      const retryAfter = error.response.headers['retry-after']
      if (retryAfter) {
        error.retryAfter = parseInt(retryAfter)
      }
    }

    return Promise.reject(error)
  }
)

// Utility function to generate device ID
const generateDeviceId = () => {
  const deviceId = 'device_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36)
  localStorage.setItem('device_id', deviceId)
  return deviceId
}

// Device info helper
const getDeviceInfo = () => {
  return {
    user_agent: navigator.userAgent,
    screen_resolution: `${screen.width}x${screen.height}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    language: navigator.language,
    platform: navigator.platform,
    device_id: localStorage.getItem('device_id') || generateDeviceId(),
    browser_name: getBrowserName(),
    viewport: `${window.innerWidth}x${window.innerHeight}`
  }
}

const getBrowserName = () => {
  const userAgent = navigator.userAgent
  if (userAgent.includes('Chrome')) return 'Chrome'
  if (userAgent.includes('Firefox')) return 'Firefox'
  if (userAgent.includes('Safari')) return 'Safari'
  if (userAgent.includes('Edge')) return 'Edge'
  if (userAgent.includes('Opera')) return 'Opera'
  return 'Unknown'
}

// Authentication API endpoints
export const authAPI = {
  // Basic authentication
  login: async (credentials) => {
    const deviceInfo = getDeviceInfo()
    return api.post('/v1/auth/login', {
      ...credentials,
      device_info: deviceInfo
    })
  },

  logout: async () => {
    try {
      await api.post('/v1/auth/logout')
    } finally {
      // Clear all session data regardless of API response
      authUtils.clearAuth()
    }
  },

  logoutAll: async () => {
    try {
      await api.post('/v1/auth/logout-all')
    } finally {
      // Clear all session data regardless of API response
      authUtils.clearAuth()
    }
  },

  checkAuth: () => api.get('/v1/verify-token'),

  // Token management
  refreshToken: () => {
    const refreshToken = localStorage.getItem('refresh_token')
    return api.post('/v1/auth/token/refresh', {
      refresh_token: refreshToken
    })
  },

  // Registration
  initiateRegistration: (userData) => {
    const deviceInfo = getDeviceInfo()
    return api.post('/v1/auth/register/initiate', {
      ...userData,
      device_info: deviceInfo
    })
  },

  completeRegistration: (verificationData) => {
    const deviceInfo = getDeviceInfo()
    return api.post('/v1/auth/register/verify', {
      ...verificationData,
      device_info: deviceInfo
    })
  },

  resendRegistrationOTP: (identifier) => {
    return api.post('/v1/auth/register/resend-otp', { identifier })
  },

  // Social authentication
  socialLogin: async (socialData) => {
    const deviceInfo = getDeviceInfo()
    return api.post('/v1/auth/social/login', {
      ...socialData,
      device_info: deviceInfo
    })
  },

  // OTP and verification
  sendOTP: (otpRequest) => {
    return api.post('/v1/auth/otp/send', otpRequest)
  },

  verifyOTP: (verificationData) => {
    return api.post('/v1/auth/otp/verify', verificationData)
  },

  // Two-Factor Authentication
  setup2FA: (setupData) => {
    return api.post('/v1/auth/2fa/setup', setupData)
  },

  verify2FA: (verificationData) => {
    const deviceInfo = getDeviceInfo()
    return api.post('/v1/auth/2fa/verify', {
      ...verificationData,
      device_info: deviceInfo
    })
  },

  disable2FA: (disableData) => {
    return api.post('/v1/auth/2fa/disable', disableData)
  },

  get2FAStatus: () => {
    return api.get('/v1/auth/2fa/status')
  },

  // Password management
  requestPasswordReset: (identifier) => {
    return api.post('/v1/auth/password/request-reset', { identifier })
  },

  resendPasswordReset: (identifier) => {
    return api.post('/v1/auth/password/resend-reset', { identifier })
  },

  verifyPasswordResetToken: (token) => {
    return api.post('/v1/auth/password/verify-reset-token', { token })
  },

  resetPassword: (resetData) => {
    return api.post('/v1/auth/password/reset/confirm', resetData)
  },

  changePassword: (passwordData) => {
    return api.put('/v1/auth/password/change', passwordData)
  },

  // Email and phone verification
  verifyEmail: (verificationData) => {
    return api.post('/v1/auth/verify/email', verificationData)
  },

  verifyPhone: (verificationData) => {
    return api.post('/v1/auth/verify/phone', verificationData)
  },

  resendEmailVerification: () => {
    return api.post('/v1/auth/verify/email/resend')
  },

  resendPhoneVerification: () => {
    return api.post('/v1/auth/verify/phone/resend')
  },

  // Session management
  getSessions: () => {
    return api.get('/v1/auth/sessions/')
  },

  terminateSession: (sessionId) => {
    return api.delete(`/v1/auth/sessions/${sessionId}`)
  },

  terminateAllOtherSessions: () => {
    return api.delete('/v1/auth/sessions/others')
  },

  // Account management
  updateProfile: (profileData) => {
    return api.put('/v1/auth/account/profile', profileData)
  },

  updateEmail: (emailData) => {
    return api.put('/v1/auth/account/email', emailData)
  },

  updatePhone: (phoneData) => {
    return api.put('/v1/auth/account/phone', phoneData)
  },

  deactivateAccount: (deactivationData) => {
    return api.post('/v1/auth/account/deactivate', deactivationData)
  },

  deleteAccount: (deletionData) => {
    return api.put('/v1/user-profile/delete-account', deletionData)
  },

  suspendAccount: (suspensionData) => {
    return api.post('/v1/user-profile/suspend', suspensionData)
  },

  // Security events
  getSecurityEvents: (params = {}) => {
    return api.get('/v1/auth/security/events', { params })
  },

  reportSuspiciousActivity: (reportData) => {
    return api.post('/v1/auth/security/report', reportData)
  },

  sendPasswordResetLink: (data) => {
    return api.post('/v1/send-reset-link', data)
  },

}

// User management API endpoints
export const userAPI = {
  getProfile: () => api.get('/v1/user-profile'),

  updateProfile: (data) => api.patch('/v1/user-profile', data),

  uploadAvatar: (file) => {
    const formData = new FormData()
    formData.append('file', file)
    return api.post('/v1/users/avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },

  getPreferences: () => api.get('/v1/users/preferences'),

  updatePreferences: (preferences) => api.put('/v1/users/preferences', preferences),

  getNotificationSettings: () => api.get('/v1/users/notifications'),

  updateNotificationSettings: (settings) => api.put('/v1/users/notifications', settings),

  getPrivacySettings: () => api.get('/v1/users/privacy'),

  updatePrivacySettings: (settings) => api.put('/v1/users/privacy', settings)
}

// Add these methods to your existing petAPI object in authAPI.js

// Updated and expanded Pet management API endpoints
export const petAPI = {
  // Get all pets for current user
  getPets: (params = {}) => api.get('/v1/pets/', { params }),

  // Create a new pet
  createPet: (petData) => api.post('/v1/pets/', petData),

  // Get a specific pet by ID
  getPet: (petId) => api.get(`/v1/pets/${petId}`),

  // Update a pet
  updatePet: (petId, petData) => api.put(`/v1/pets/${petId}`, petData),

  // Delete a pet (soft delete)
  deletePet: (petId) => api.delete(`/v1/pets/${petId}`),

  // Upload pet photo
  uploadPetPhoto: (petId, file, isProfilePhoto = false) => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('is_profile_photo', isProfilePhoto)
    return api.post(`/v1/pets/${petId}/photo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },

  // Get pet statistics
  getPetStatistics: () => api.get('/v1/pets/statistics/overview'),

  // Get breeds by species
  getBreedsBySpecies: (species) => api.get(`/v1/pets/breeds/${species}`),

  // Search/filter pets
  searchPets: (searchParams) => api.get('/v1/pets/', { params: searchParams }),

  // Medical records
  createMedicalRecord: (petId, recordData) =>
    api.post(`/v1/pets/${petId}/medical-records`, recordData),

  getMedicalRecords: (petId) =>
    api.get(`/v1/pets/${petId}/medical-records`),

  updateMedicalRecord: (petId, recordId, recordData) =>
    api.put(`/v1/pets/${petId}/medical-records/${recordId}`, recordData),

  deleteMedicalRecord: (petId, recordId) =>
    api.delete(`/v1/pets/${petId}/medical-records/${recordId}`),

  // Vaccinations
  createVaccination: (petId, vaccinationData) =>
    api.post(`/v1/pets/${petId}/vaccinations`, vaccinationData),

  getVaccinations: (petId) =>
    api.get(`/v1/pets/${petId}/vaccinations`),

  updateVaccination: (petId, vaccinationId, vaccinationData) =>
    api.put(`/v1/pets/${petId}/vaccinations/${vaccinationId}`, vaccinationData),

  deleteVaccination: (petId, vaccinationId) =>
    api.delete(`/v1/pets/${petId}/vaccinations/${vaccinationId}`),

  // Family relationships
  createFamilyRelationship: (petId, relationshipData) =>
    api.post(`/v1/pets/${petId}/family`, relationshipData),

  getFamilyRelationships: (petId) =>
    api.get(`/v1/pets/${petId}/family`),

  deleteFamilyRelationship: (petId, relationshipId) =>
    api.delete(`/v1/pets/${petId}/family/${relationshipId}`),

  // Bulk operations
  bulkDeletePets: (petIds) =>
    api.post('/v1/pets/bulk/delete', { pet_ids: petIds, confirm_deletion: true }),

  // Export/Import (for future use)
  exportPets: (exportParams) =>
    api.post('/v1/pets/export', exportParams),

  importPets: (importData) =>
    api.post('/v1/pets/import', importData),

  // Get complete medical history for a pet
  getCompleteMedicalHistory: (petId) =>
    api.get(`/api/v1/pet-health/medical-history/${petId}`)
}

// Pet-related utility functions
export const petUtils = {
  // Format age display
  formatAge: (ageYears, ageMonths, dateOfBirth) => {
    if (ageYears && ageMonths) {
      return `${ageYears} years, ${ageMonths} months`
    } else if (ageYears) {
      return `${ageYears} years`
    } else if (ageMonths) {
      return `${ageMonths} months`
    } else if (dateOfBirth) {
      const today = new Date()
      const birth = new Date(dateOfBirth)
      const diffTime = Math.abs(today - birth)
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
      const years = Math.floor(diffDays / 365)
      const months = Math.floor((diffDays % 365) / 30)

      if (years > 0) {
        return `${years} years, ${months} months`
      } else {
        return `${months} months`
      }
    }
    return 'Age unknown'
  },

  // Get health status color
  getHealthStatusColor: (status) => {
    const colors = {
      'HEALTHY': '#10B981',
      'NEEDS_CHECKUP': '#F59E0B',
      'UNDER_TREATMENT': '#EF4444',
      'CHRONIC_CONDITION': '#8B5CF6',
      'RECOVERING': '#06B6D4',
      'UNKNOWN': '#6B7280'
    }
    return colors[status] || colors.UNKNOWN
  },

  // Get species emoji
  getSpeciesEmoji: (species) => {
    const emojis = {
      'DOG': '🐕',
      'CAT': '🐱',
      'BIRD': '🐦',
      'FISH': '🐠',
      'RABBIT': '🐰',
      'HAMSTER': '🐹',
      'GUINEA_PIG': '🐹',
      'TURTLE': '🐢',
      'REPTILE': '🦎',
      'OTHER': '🐾'
    }
    return emojis[species] || emojis.OTHER
  },

  // Validate pet data before submission
  validatePetData: (petData) => {
    const errors = {}

    if (!petData.name?.trim()) {
      errors.name = 'Pet name is required'
    }

    if (!petData.species) {
      errors.species = 'Species is required'
    }

    if (!petData.gender) {
      errors.gender = 'Gender is required'
    }

    if (petData.weight && (isNaN(petData.weight) || parseFloat(petData.weight) <= 0)) {
      errors.weight = 'Weight must be a positive number'
    }

    if (petData.age_years && (isNaN(petData.age_years) || parseInt(petData.age_years) < 0)) {
      errors.age_years = 'Age years must be non-negative'
    }

    if (petData.age_months && (isNaN(petData.age_months) || parseInt(petData.age_months) < 0 || parseInt(petData.age_months) > 11)) {
      errors.age_months = 'Age months must be between 0-11'
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    }
  },

  // Format pet data for API submission
  formatPetDataForAPI: (formData) => {
    return {
      name: formData.name?.trim(),
      species: formData.species,
      breed: formData.breed || null,
      color: formData.color || null,
      gender: formData.gender,
      provider_name: formData.provider_name || null,
      provider_number: formData.provider_number || null,
      policy_number: formData.policy_number || null,
      microchip_id: formData.microchip_id || null,
      smart_collar_id: formData.smart_collar_id || null,
      weight: formData.weight ? parseFloat(formData.weight) : null,
      is_spayed_neutered: Boolean(formData.is_spayed_neutered),
      age_years: formData.age_years ? parseInt(formData.age_years) : null,
      age_months: formData.age_months ? parseInt(formData.age_months) : null,
      adoption_date: formData.adoption_date || null,
      interests: formData.interests || []
    }
  }
}

// Utility functions for authentication state management
export const authUtils = {
  // Check if user is authenticated
  isAuthenticated: () => {
    const token = localStorage.getItem('access_token');
    const userData = localStorage.getItem('user_data');
    return !!(token && userData)
  },

  // Get current user data
  getCurrentUser: () => {
    try {
      const userData = localStorage.getItem('user_data')
      return userData ? JSON.parse(userData) : null
    } catch (error) {
      console.error('Error parsing user data:', error)
      return null
    }
  },

  // Update current user data
  updateCurrentUser: (userData) => {
    localStorage.setItem('user_data', JSON.stringify(userData))
  },

  // Check if user has specific role/permission
  hasRole: (role) => {
    const user = authUtils.getCurrentUser()
    return user && user.user_type === role
  },

  // Check if user has completed onboarding
  hasCompletedOnboarding: () => {
    const user = authUtils.getCurrentUser()
    return user && user.onboarding_completed
  },

  // Clear all authentication data
  clearAuth: () => {
    // Clear localStorage
    localStorage.clear()

    // Clear sessionStorage
    sessionStorage.clear()

    // Clear all cookies
    document.cookie.split(";").forEach(function (c) {
      document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
    })

    // Clear specific auth-related cookies
    const cookiesToClear = [
      'access_token',
      'refresh_token',
      'user_data',
      'device_id',
      'last_login',
      'demo_mode',
      'intended_route',
      'lang',
      'theme',
      'preferences'
    ]

    cookiesToClear.forEach(cookieName => {
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname}`
    })

  },

  // Get token expiration time
  getTokenExpiration: () => {
    const token = localStorage.getItem('access_token')
    if (!token) return null

    try {
      const payload = JSON.parse(atob(token.split('.')[1]))
      return new Date(payload.exp * 1000)
    } catch (error) {
      console.error('Error parsing token:', error)
      return null
    }
  },

  // Check if token is expired
  isTokenExpired: () => {
    const expiration = authUtils.getTokenExpiration()
    return expiration ? new Date() >= expiration : true
  },

  // Get time until token expires (in minutes)
  getTokenTimeRemaining: () => {
    const expiration = authUtils.getTokenExpiration()
    if (!expiration) return 0

    const now = new Date()
    const diff = expiration.getTime() - now.getTime()
    return Math.max(0, Math.floor(diff / (1000 * 60)))
  }
}

// Google OAuth integration
export const googleAuth = {
  initialize: () => {
    return new Promise((resolve, reject) => {
      if (typeof gapi === 'undefined') {
        reject(new Error('Google API not loaded'))
        return
      }

      gapi.load('auth2', () => {
        gapi.auth2.init({
          client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID
        }).then(resolve).catch(reject)
      })
    })
  },

  signIn: async () => {
    try {
      const authInstance = gapi.auth2.getAuthInstance()
      const user = await authInstance.signIn()
      const profile = user.getBasicProfile()
      const authResponse = user.getAuthResponse()

      return {
        provider: 'google',
        provider_user_id: profile.getId(),
        access_token: authResponse.access_token,
        provider_email: profile.getEmail(),
        provider_profile_data: {
          name: profile.getName(),
          given_name: profile.getGivenName(),
          family_name: profile.getFamilyName(),
          picture: profile.getImageUrl(),
          email: profile.getEmail(),
          email_verified: true
        }
      }
    } catch (error) {
      console.error('Google sign-in failed:', error)
      throw error
    }
  },

  signOut: async () => {
    try {
      const authInstance = gapi.auth2.getAuthInstance()
      await authInstance.signOut()
    } catch (error) {
      console.error('Google sign-out failed:', error)
    }
  }
}

// Apple Sign-In integration
export const appleAuth = {
  signIn: async () => {
    return new Promise((resolve, reject) => {
      if (typeof AppleID === 'undefined') {
        reject(new Error('Apple Sign-In not loaded'))
        return
      }

      AppleID.auth.signIn({
        clientId: import.meta.env.VITE_APPLE_CLIENT_ID,
        scope: 'name email',
        redirectURI: import.meta.env.VITE_APPLE_REDIRECT_URI,
        state: 'signin',
        nonce: 'nonce_' + Math.random().toString(36).substr(2, 9),
        usePopup: true
      }).then((response) => {
        resolve({
          provider: 'apple',
          provider_user_id: response.user,
          access_token: response.authorization.id_token,
          provider_email: response.email,
          provider_profile_data: {
            name: response.name ? {
              firstName: response.name.firstName,
              lastName: response.name.lastName
            } : null,
            email: response.email
          }
        })
      }).catch(reject)
    })
  }
}

// Error handling utilities
export const apiErrorHandler = {
  // Parse API error response
  parseError: (error) => {
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response
      return {
        status,
        message: data.detail || data.message || 'An error occurred',
        errors: data.errors || {},
        code: data.code || null
      }
    } else if (error.request) {
      // Network error
      return {
        status: 0,
        message: 'Network error. Please check your connection.',
        errors: {},
        code: 'NETWORK_ERROR'
      }
    } else {
      // Other error
      return {
        status: 0,
        message: error.message || 'An unexpected error occurred',
        errors: {},
        code: 'UNKNOWN_ERROR'
      }
    }
  },

  // Handle authentication errors
  handleAuthError: (error) => {
    const parsedError = apiErrorHandler.parseError(error)

    switch (parsedError.status) {
      case 401:
        authUtils.clearAuth()
        if (window.location.pathname !== '/auth/login') {
          localStorage.setItem('intended_route', window.location.pathname)
          window.location.href = '/auth/login'
        }
        break
      case 403:
        // Forbidden - user doesn't have permission
        break
      case 429:
        // Rate limited
        break
      default:
        break
    }

    return parsedError
  }
}

export default api